import BoldText from '@/components/BoldText';
import MediumText from '@/components/MediumText';
import { limitPlan } from '@/constants';
import { BREAKPOINT, Plan, Touchpoint } from '@/constants/enum';
import { PATH } from '@/constants/path';
import useTrackTouchpoint from '@/hooks/useTrackTouchpoint';
import { apiCaller } from '@/redux/query';
import { isSkipApiSelector } from '@/redux/slice/auth.slice';
import { isLoadingGeneralSettingsSelector } from '@/redux/slice/loading';
import ruleSlice from '@/redux/slice/rule.slice';
import { dataSettingsSelector } from '@/redux/slice/settings.slice';
import { Badge, Button, Card, Icon, SkeletonBodyText } from '@shopify/polaris';
import { CheckCircleIcon, LockFilledIcon, XCircleIcon } from '@shopify/polaris-icons';
import { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';

function CreateGeneralRule() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const dataSettings = useSelector(dataSettingsSelector);
  const isSkip = useSelector(isSkipApiSelector);
  const isMobile = useMediaQuery({ maxWidth: BREAKPOINT.XS });
  const { data: totalRule, isLoading } = apiCaller.useRuleCountQuery(undefined, { skip: isSkip });
  const isLoadingSetting = useSelector(isLoadingGeneralSettingsSelector);
  const trackTouchPoint = useTrackTouchpoint();

  const numberOfRule = totalRule?.data?.countRule || 0;

  const contentBadge = useMemo(() => {
    if (!dataSettings?.plan) {
      return {
        content: 'Action required',
        isCritical: true,
        isTrackTouchpoint: false,
      };
    }

    const planConfig = {
      [Plan.FREE]: {
        content: `Free Plan limit: ${numberOfRule}/${limitPlan.generalRule[Plan.FREE]}`,
        isCritical: numberOfRule >= limitPlan.generalRule[Plan.FREE],
        isTrackTouchpoint: true,
      },
      [Plan.STANDARD]: {
        content: `Standard Plan limit: ${numberOfRule}/${limitPlan.generalRule[Plan.STANDARD]}`,
        isCritical: numberOfRule >= limitPlan.generalRule[Plan.STANDARD],
        isTrackTouchpoint: true,
      },
      [Plan.PRO]: {
        content: `Pro Plan: ${numberOfRule}/Unlimited`,
        isCritical: false,
        isTrackTouchpoint: false,
      },
      [Plan.SHOPIFY_PLUS]: {
        content: `Plus Plan: ${numberOfRule}/Unlimited`,
        isCritical: false,
        isTrackTouchpoint: false,
      },
    };

    return (
      planConfig[dataSettings.plan as keyof typeof planConfig] || {
        content: 'Action required',
        isCritical: true,
        isTrackTouchpoint: false,
      }
    );
  }, [numberOfRule, dataSettings?.plan]);

  const handleClick = useCallback(() => {
    if (contentBadge.isCritical) {
      navigate(PATH.PRICING_PLAN);
    } else {
      navigate(PATH.CREATE_RULE, { state: { from: PATH.DEFAULT } });
      dispatch(ruleSlice.actions.handleInit());
    }
  }, [contentBadge.isCritical, navigate, dispatch]);

  const handleBadgeClick = useCallback(() => {
    if (contentBadge.isCritical) {
      navigate(PATH.PRICING_PLAN);
      if (contentBadge.isTrackTouchpoint) {
        trackTouchPoint(Touchpoint.ChecklistGeneralRuleLimit);
      }
    }
  }, [contentBadge.isCritical, contentBadge.isTrackTouchpoint, navigate, trackTouchPoint]);

  // const badgeTone = useMemo(() => {
  //   if (contentBadge.isCritical || !numberOfRule) return 'critical';
  //   if (dataSettings.plan === Plan.PRO || dataSettings.plan === Plan.SHOPIFY_PLUS) return 'info';
  //   return 'attention';
  // }, [contentBadge.isCritical, numberOfRule, dataSettings.plan]);

  const isHasRule = useMemo(() => {
    return numberOfRule > 0;
  }, [numberOfRule]);

  return isLoading ? (
    <Card>
      <SkeletonBodyText lines={4} />
    </Card>
  ) : (
    <Card>
      <div className="content-between">
        <div className={`d-flex ${isMobile ? 'flex-column' : 'flex-row align-center'}`}>
          <div className="d-flex align-center">
            {!isHasRule ? <Icon source={XCircleIcon} tone="warning" /> : <Icon source={CheckCircleIcon} tone="success" />}
            <div className="ml-8">
              <BoldText>Create estimate rules</BoldText>
            </div>
          </div>
          <div
            onClick={handleBadgeClick}
            className={`${isMobile ? 'mt-8' : 'ml-8'} ${contentBadge.isCritical ? 'cursor-pointer' : ''}`}
          >
            <Badge tone={isHasRule ? 'info' : 'attention'} icon={contentBadge.isCritical ? LockFilledIcon : undefined}>
              {isHasRule ? contentBadge.content : 'Action required'}
            </Badge>
          </div>
        </div>
        {!contentBadge.isCritical ? (
          <Button onClick={handleClick} variant="primary" loading={isLoading || isLoadingSetting}>
            Create a rule
          </Button>
        ) : null}
      </div>
      {isHasRule ? (
        <div className="mt-8 w-full">
          <MediumText>
            {contentBadge.isCritical
              ? 'You’ve reached limit!'
              : `Have more rules to inform buyers of all available shipping options or customize message for different locations and
            products to boost conversion.`}
          </MediumText>
        </div>
      ) : (
        'Displaying estimated delivery based on countries, zipcodes, products, collections, and more.'
      )}
    </Card>
  );
}

export default memo(CreateGeneralRule);
